import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../../services/ApiService';
import type { EventClass, EventRegistration, RegistrationReviewData } from '../../types/event';
import RegistrationStatusBadge from '../ui/RegistrationStatusBadge';
import Button from '../ui/Button';
import { organizerStatusLabel } from '../../utils/registrationStatus';

interface EventReviewQueueProps {
  eventId: number;
  classes: EventClass[];
  onChanged?: () => void;
}

const STATUS_FILTERS = [
  { value: 'pending_approval', label: 'Pending review' },
  { value: 'approved', label: 'Approved' },
  { value: 'payment_pending', label: 'Awaiting payment' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

const EventReviewQueue: React.FC<EventReviewQueueProps> = ({ eventId, classes, onChanged }) => {
  const [data, setData] = useState<RegistrationReviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('pending_approval');
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState<number | ''>('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busyId, setBusyId] = useState<number | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [rejectId, setRejectId] = useState<number | null>(null);
  const [internalNote, setInternalNote] = useState('');
  const [rejectNote, setRejectNote] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getRegistrationReview(eventId, {
        status: statusFilter,
        q: search.trim() || undefined,
        class_id: classFilter === '' ? undefined : Number(classFilter),
      });
      setData(result);
      setSelected(new Set());
    } catch (err: any) {
      setError(err.message || 'Failed to load review queue');
    } finally {
      setLoading(false);
    }
  }, [eventId, statusFilter, search, classFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const pendingItems = useMemo(
    () => (data?.items || []).filter((r) => ['pending_approval', 'submitted'].includes(r.status)),
    [data?.items]
  );

  const vehicleLabel = (r: EventRegistration) => {
    const v = r.vehicle;
    if (!v) return 'Unknown vehicle';
    return v.nickname || `${v.year} ${v.make} ${v.model}`.trim();
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApprove = async (id: number) => {
    try {
      setBusyId(id);
      setError(null);
      await apiService.approveRegistration(id, internalNote.trim() || undefined);
      setInternalNote('');
      await load();
      onChanged?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (id: number) => {
    try {
      setBusyId(id);
      setError(null);
      await apiService.rejectRegistration(id, {
        internal_note: internalNote.trim() || undefined,
        public_reject_note: rejectNote.trim() || undefined,
      });
      setRejectId(null);
      setInternalNote('');
      setRejectNote('');
      await load();
      onChanged?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleBulkApprove = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Approve ${ids.length} registration(s)?`)) return;
    try {
      setBulkBusy(true);
      setError(null);
      await apiService.bulkApproveRegistrations(eventId, ids);
      await load();
      onChanged?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBulkBusy(false);
    }
  };

  const inputClass =
    'w-full bg-[#0a0a0a] border border-white/[0.06] rounded-xl px-3.5 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/15 min-h-[44px]';

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search owner, vehicle, class…"
          className={inputClass}
          aria-label="Search registrations"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={inputClass}
          aria-label="Filter by status"
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        {classes.length > 0 ? (
          <select
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value ? Number(e.target.value) : '')}
            className={inputClass}
            aria-label="Filter by class"
          >
            <option value="">All classes</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        ) : null}
        <Button variant="secondary" size="sm" onClick={() => void load()} className="min-h-[44px] shrink-0">
          Refresh
        </Button>
      </div>

      {selected.size > 0 && statusFilter === 'pending_approval' ? (
        <div className="flex flex-wrap items-center gap-3 p-3 rounded-xl border border-orange-500/30 bg-orange-950/20">
          <span className="text-sm text-orange-200">{selected.size} selected</span>
          <Button variant="primary" size="sm" loading={bulkBusy} onClick={() => void handleBulkApprove()}>
            Bulk approve
          </Button>
          <button
            type="button"
            className="text-sm text-gray-400 hover:text-white min-h-[44px]"
            onClick={() => setSelected(new Set())}
          >
            Clear selection
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="alert-error text-sm" role="alert">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-gray-500">Loading review queue…</p>
      ) : !data?.items.length ? (
        <p className="text-sm text-gray-500">No registrations match this filter.</p>
      ) : (
        <ul className="space-y-3">
          {data.items.map((r) => {
            const isPending = ['pending_approval', 'submitted'].includes(r.status);
            const showRejectForm = rejectId === r.id;
            return (
              <li key={r.id} className="rounded-xl border border-white/[0.06] bg-[#0c0c0c]/50 p-4">
                <div className="flex items-start gap-3">
                  {isPending && statusFilter === 'pending_approval' ? (
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggleSelect(r.id)}
                      className="mt-1 rounded border-white/[0.10] bg-[#141414] text-orange-500"
                      aria-label={`Select ${vehicleLabel(r)}`}
                    />
                  ) : null}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="font-medium text-white">{vehicleLabel(r)}</p>
                        <p className="text-sm text-gray-500">@{r.owner?.username ?? 'unknown'}</p>
                      </div>
                      <RegistrationStatusBadge
                        status={r.status}
                        label={r.label || organizerStatusLabel(r.status)}
                        compact
                      />
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-gray-500">
                      {r.class?.name ? <span>{r.class.name}</span> : null}
                      {r.notes ? <span className="italic">"{r.notes}"</span> : null}
                      {r.created_at ? (
                        <span>
                          Applied {new Date(r.created_at.replace(' ', 'T')).toLocaleDateString()}
                        </span>
                      ) : null}
                    </div>
                    {r.internal_note ? (
                      <p className="text-xs text-gray-600 mt-2">Internal: {r.internal_note}</p>
                    ) : null}
                  </div>
                </div>

                {isPending ? (
                  <div className="mt-4 space-y-3 border-t border-white/[0.06] pt-4">
                    {!showRejectForm ? (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          loading={busyId === r.id}
                          onClick={() => void handleApprove(r.id)}
                          className="min-h-[40px]"
                        >
                          Approve
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={busyId === r.id}
                          onClick={() => setRejectId(r.id)}
                          className="min-h-[40px]"
                        >
                          Reject
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <label className="block text-xs text-gray-400">
                          Internal note (organizer only)
                          <textarea
                            value={internalNote}
                            onChange={(e) => setInternalNote(e.target.value)}
                            className={`${inputClass} mt-1 min-h-[72px]`}
                            rows={2}
                          />
                        </label>
                        <label className="block text-xs text-gray-400">
                          Message to builder (optional)
                          <textarea
                            value={rejectNote}
                            onChange={(e) => setRejectNote(e.target.value)}
                            className={`${inputClass} mt-1 min-h-[72px]`}
                            rows={2}
                            placeholder="Optional note shown to the builder"
                          />
                        </label>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="danger"
                            size="sm"
                            loading={busyId === r.id}
                            onClick={() => void handleReject(r.id)}
                          >
                            Confirm reject
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setRejectId(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {statusFilter === 'pending_approval' && pendingItems.length > 0 ? (
        <p className="text-xs text-gray-600">
          {pendingItems.length} application(s) waiting for review.
        </p>
      ) : null}
    </div>
  );
};

export default EventReviewQueue;
